import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CreativeTypes } from '@bynder-studio/render-core';
import {
    collectAlertMessagesFromDesignList,
    getExportableDesignsFromDesignList,
    getIncompatibleDesignsCount,
    getPreferredConfigurationIds,
    getProfileCompatibleDesigns,
    getUniqueCreativeTypesFromDesignList,
} from './utils';
import { type ExportResult } from './types';
import { type RenditionConfiguration } from 'packages/services/renditionsService';
import { PresetData } from './Preset';
import { useFlyInNotificationContext } from 'packages/FlyInNotifications/FlyInNotificationsContext';
import { type ExportPayload } from 'packages/types/exports';
import { ExportPresetValue } from 'packages/hooks/useExportPreset';
import { isAbortError } from 'packages/helpers/helpers';
import { sendAppcuesEvent } from 'packages/helpers/RouteWithAppcues';
import { Creative } from 'packages/store/creatives/types';
import useAccessRights from 'packages/hooks/useAccessRights';
import exportService from 'packages/services/exportService';

function useDesignsBulkExportModal(designs: Creative[], isOpen: boolean, onClose: () => void) {
    const { isBynderScopeAllowed } = useAccessRights();
    const exportableDesigns = useMemo(() => getExportableDesignsFromDesignList(designs), [designs]);
    const creativeTypes = useMemo(() => getUniqueCreativeTypesFromDesignList(designs), [designs]);
    const preferredConfigurationIdsByType = useMemo<Record<CreativeTypes, number[] | undefined>>(
        () => ({
            [CreativeTypes.IMAGE]: getPreferredConfigurationIds(exportableDesigns, CreativeTypes.IMAGE),
            [CreativeTypes.VIDEO]: getPreferredConfigurationIds(exportableDesigns, CreativeTypes.VIDEO),
        }),
        [exportableDesigns],
    );
    const [isLoadingRenditions, setIsLoadingRenditions] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [isTranscribeEnabled, setIsTranscribeEnabled] = useState(false);
    const [exportResults, setExportResults] = useState<ExportResult[]>([]);
    const alertMessage = useMemo(
        () => collectAlertMessagesFromDesignList(designs, exportResults),
        [designs, exportResults],
    );
    const [selectedProfiles, setSelectedProfiles] = useState<Record<CreativeTypes, RenditionConfiguration> | undefined>(
        undefined,
    );
    const selectedPresetData = useRef<PresetData>(null);
    const { showNotification } = useFlyInNotificationContext();

    const profileCompatibleDesigns = useMemo(
        () => getProfileCompatibleDesigns(exportableDesigns, selectedProfiles),
        [exportableDesigns, selectedProfiles],
    );

    const incompatibleDesignsCount = useMemo(
        () => getIncompatibleDesignsCount(exportableDesigns, selectedProfiles),
        [exportableDesigns, selectedProfiles],
    );

    const isDownloadBlocked = useMemo(
        () =>
            profileCompatibleDesigns.some(
                (design) =>
                    design.exportIntegrations?.find((item) => item?.channelType === 'DOWNLOAD')?.enabled === false,
            ),
        [profileCompatibleDesigns],
    );

    const isWaitingRoomBlocked = useMemo(
        () =>
            !isBynderScopeAllowed(['asset:write']) ||
            profileCompatibleDesigns.some(
                (design) =>
                    design.exportIntegrations?.find((item) => item?.channelType === 'BYNDER')?.enabled === false,
            ),
        [isBynderScopeAllowed, profileCompatibleDesigns],
    );

    const handleRenditionsLoad = useCallback(() => {
        setIsLoadingRenditions(false);
    }, []);

    const handleSelectProfile = useCallback((profiles: Record<CreativeTypes, RenditionConfiguration>) => {
        setSelectedProfiles(profiles);
    }, []);

    const handleSelectPreset = useCallback((presetData: PresetData) => {
        selectedPresetData.current = presetData;
    }, []);

    const handleTranscribeChange = useCallback(() => {
        setIsTranscribeEnabled((prev) => !prev);
    }, []);

    const handleExport = useCallback(() => {
        if (!selectedPresetData.current || !selectedProfiles) {
            return;
        }

        setIsExporting(true);
        const { preset, bynderAccountId } = selectedPresetData.current;

        Promise.all(
            profileCompatibleDesigns.map((design) => {
                const selectedProfile = selectedProfiles[design.creativeType];

                if (!selectedProfile) {
                    return null;
                }

                let payload: ExportPayload = {
                    name: preset.label,
                    type: preset.value,
                    variationIds: design.variationIds,
                };

                if (preset.value === ExportPresetValue.BYNDER) {
                    payload = {
                        ...payload,
                        renditionConfigurationId: selectedProfile.id,
                        bynderAccountId,
                    };
                } else if (preset.value === ExportPresetValue.DOWNLOAD) {
                    payload = {
                        ...payload,
                        renditionConfigurationId: selectedProfile.id,
                    };
                }

                return exportService
                    .createExportJob(payload)
                    .then(({ json, status }) => {
                        if (status === 200) {
                            const notificationId = json.id;

                            showNotification({
                                notificationId,
                                show: true,
                                creativeName: design.name,
                                thumbnailUrl: design.thumbnailImage,
                                exportType: preset.value,
                            });

                            return {
                                data: json,
                                success: true,
                                designId: design.id,
                            } as ExportResult;
                        }

                        return {
                            ...(json as unknown as { message: string }),
                            success: false,
                            designId: design.id,
                        } as ExportResult;
                    })
                    .catch((err) => {
                        if (isAbortError(err)) {
                            return null;
                        }

                        return {
                            message: err.message,
                            success: false,
                            designId: design.id,
                        } as ExportResult;
                    });
            }),
        )
            .then((result) => {
                const filteredResult = result.filter((r): r is ExportResult => r !== null);

                if (
                    filteredResult.length === profileCompatibleDesigns.length &&
                    filteredResult.every((r) => r.success)
                ) {
                    sendAppcuesEvent('Bulk export job created', {
                        designIds: profileCompatibleDesigns.map((d) => d.id),
                        image: selectedProfiles[CreativeTypes.IMAGE]?.displayName,
                        video: selectedProfiles[CreativeTypes.VIDEO]?.displayName,
                        name: preset.label,
                        type: preset.value,
                    });

                    onClose();
                } else {
                    setExportResults(filteredResult);
                }
            })
            .finally(() => {
                setIsExporting(false);
            });
    }, [profileCompatibleDesigns, selectedProfiles, onClose]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        return () => {
            setIsLoadingRenditions(true);
            setIsExporting(false);
        };
    }, [isOpen]);

    return {
        handleExport,
        isExporting,
        isLoadingRenditions,
        isDownloadBlocked,
        isWaitingRoomBlocked,
        isTranscribeEnabled,
        exportableDesignsLength: profileCompatibleDesigns.length,
        incompatibleDesignsCount,
        exportResults,
        creativeTypes,
        alertMessage,
        selectedProfiles,
        handleSelectPreset,
        handleRenditionsLoad,
        handleSelectProfile,
        handleTranscribeChange,
        preferredConfigurationIdsByType,
    };
}

export default useDesignsBulkExportModal;
